import { ChevronDown, ExternalLink, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { formatKoreanDate } from '../lib/date'
import type { FaqContent, NoticeContent } from '../types/content'

interface FaqViewProps {
  faq: FaqContent
  notices: NoticeContent
}

type FaqTab = 'faq' | 'notices'

export default function FaqView({ faq, notices }: FaqViewProps) {
  const [tab, setTab] = useState<FaqTab>('faq')
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ko-KR')
    if (!normalized) return faq.items
    return faq.items.filter((item) =>
      [item.question, item.answer, item.category, ...item.keywords]
        .join(' ')
        .toLocaleLowerCase('ko-KR')
        .includes(normalized),
    )
  }, [faq.items, query])

  return (
    <div className="view-stack">
      <div className="segmented" role="tablist" aria-label="FAQ와 변경 소식">
        <button type="button" role="tab" aria-selected={tab === 'faq'} onClick={() => setTab('faq')}>FAQ</button>
        <button type="button" role="tab" aria-selected={tab === 'notices'} onClick={() => setTab('notices')}>변경 소식</button>
      </div>

      {tab === 'faq' ? (
        <section aria-labelledby="faq-title">
          <div className="section-heading">
            <p className="eyebrow">자주 묻는 질문</p>
            <h2 id="faq-title">궁금한 내용을 찾아보세요</h2>
          </div>
          <label className="search-field">
            <Search aria-hidden="true" />
            <span className="sr-only">FAQ 검색</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="예: 신청 기간, 중복 지원" type="search" />
          </label>
          <div className="faq-list">
            {filtered.map((item) => (
              <details key={item.id} className="faq-item">
                <summary><span><small>{item.category}</small>{item.question}</span><ChevronDown aria-hidden="true" /></summary>
                <div><p>{item.answer}</p><a href={item.sourceUrl} target="_blank" rel="noreferrer">공식 원문 확인 <ExternalLink aria-hidden="true" size={15} /></a></div>
              </details>
            ))}
            {filtered.length === 0 && <p className="empty-state">검색 결과가 없습니다. 다른 단어로 찾아보세요.</p>}
          </div>
        </section>
      ) : (
        <section aria-labelledby="notices-title">
          <div className="section-heading">
            <p className="eyebrow">변경 이력</p>
            <h2 id="notices-title">무엇이 달라졌는지 알려드려요</h2>
          </div>
          <ol className="notice-list">
            {notices.items.map((item) => (
              <li key={item.id}>
                <time dateTime={item.publishedAt}>{formatKoreanDate(item.publishedAt)}</time>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
                <a href={item.sourceUrl} target="_blank" rel="noreferrer">출처 확인 <ExternalLink aria-hidden="true" size={15} /></a>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}
